import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getApiBaseUrl } from '../../../core/config/api';
import { useRealtimeInvalidation } from '../../../shared/realtime/RealtimeProvider';

/**
 * Gestion des rôles depuis le panel : créer / renommer / supprimer un rôle et éditer sa
 * matrice de permissions (auth_role_permission). Piloté par la capacité `roles.manage`
 * (Founder par défaut). Le Founder est protégé (superutilisateur, non éditable/supprimable).
 * Voir docs/CONCEPTION_PERMISSIONS_ROLES.md.
 */

interface PermissionDef {
  code: string;
  label: string;
  description: string;
  category: string;
}
interface RoleItem {
  id: number;
  role_name: string;
  description: string | null;
  rank: number;
  is_system: boolean;
  user_count: number;
  permissions: string[];
}
interface RolesData {
  roles: RoleItem[];
  catalogue: PermissionDef[];
}

const CATEGORY_LABELS: Record<string, string> = {
  panel: 'Panel & tableau de bord',
  moderation: 'Modération',
  users: 'Utilisateurs & rôles',
  cta: 'Scénarios / CTA',
  game_config: 'Configuration du jeu',
  economy: 'Économie',
  recruitment: 'Recrutement',
  team: 'Équipe & support',
  system: 'Système & infrastructure',
  general: 'Autres',
};

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } as const;
};

async function readJson(res: Response): Promise<any> {
  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('application/json')) {
    throw new Error(
      res.status === 404
        ? 'Route /api/panel/roles introuvable : le serveur doit être redéployé (build:server + redémarrage).'
        : `Réponse serveur invalide (HTTP ${res.status}).`,
    );
  }
  return res.json();
}

const RolesManagement: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const base = `${getApiBaseUrl(true)}/panel/roles`;

  const [data, setData] = useState<RolesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [permDraft, setPermDraft] = useState<Set<string>>(new Set());
  const [savingPerms, setSavingPerms] = useState(false);

  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editRank, setEditRank] = useState<number>(0);
  const [savingInfos, setSavingInfos] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);

  const flash = (msg: string) => {
    setNotice(msg);
    setError(null);
    window.setTimeout(() => setNotice(null), 2500);
  };

  const fetchAll = useCallback(async () => {
    try {
      const res = await fetch(base, { headers: authHeaders(), credentials: 'include' });
      const json = await readJson(res);
      if (!res.ok || !json.success) throw new Error(json.error || 'Erreur de chargement');
      setData(json.data);
    } catch (err: any) {
      setError(err?.message || 'Erreur réseau');
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  useRealtimeInvalidation('roles', () => void fetchAll());

  const selected = useMemo(
    () => data?.roles.find((r) => r.id === selectedId) ?? null,
    [data, selectedId],
  );

  // (Ré)initialise les brouillons quand le rôle sélectionné change ou que les données rechargent.
  useEffect(() => {
    if (!selected) return;
    setPermDraft(new Set(selected.permissions));
    setEditName(selected.role_name);
    setEditDesc(selected.description ?? '');
    setEditRank(selected.rank);
  }, [selected]);

  const catByCategory = useMemo(() => {
    const map = new Map<string, PermissionDef[]>();
    for (const p of data?.catalogue ?? []) {
      const arr = map.get(p.category) ?? [];
      arr.push(p);
      map.set(p.category, arr);
    }
    return Array.from(map.entries());
  }, [data]);

  const createRole = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const res = await fetch(base, {
        method: 'POST',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify({ role_name: name, description: newDesc.trim() || null }),
      });
      const json = await readJson(res);
      if (!res.ok || !json.success) throw new Error(json.error || 'Erreur');
      setNewName('');
      setNewDesc('');
      setShowCreate(false);
      await fetchAll();
      setSelectedId(json.data.id);
      flash('Rôle créé');
    } catch (err: any) {
      setError(err?.message || 'Erreur réseau');
    } finally {
      setCreating(false);
    }
  };

  const saveInfos = async () => {
    if (!selected) return;
    setSavingInfos(true);
    try {
      const res = await fetch(`${base}/${selected.id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify({ role_name: editName.trim(), description: editDesc.trim() || null, rank: editRank }),
      });
      const json = await readJson(res);
      if (!res.ok || !json.success) throw new Error(json.error || 'Erreur');
      await fetchAll();
      flash('Rôle mis à jour');
    } catch (err: any) {
      setError(err?.message || 'Erreur réseau');
    } finally {
      setSavingInfos(false);
    }
  };

  const deleteRole = async () => {
    if (!selected) return;
    if (!window.confirm(`Supprimer le rôle « ${selected.role_name} » ? Cette action est définitive.`)) return;
    try {
      const res = await fetch(`${base}/${selected.id}`, { method: 'DELETE', headers: authHeaders(), credentials: 'include' });
      const json = await readJson(res);
      if (!res.ok || !json.success) throw new Error(json.error || 'Erreur');
      setSelectedId(null);
      await fetchAll();
      flash('Rôle supprimé');
    } catch (err: any) {
      setError(err?.message || 'Erreur réseau');
    }
  };

  const togglePerm = (code: string) => {
    setPermDraft((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const savePerms = async () => {
    if (!selected) return;
    setSavingPerms(true);
    try {
      const res = await fetch(`${base}/${selected.id}/permissions`, {
        method: 'PUT',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify({ codes: Array.from(permDraft) }),
      });
      const json = await readJson(res);
      if (!res.ok || !json.success) throw new Error(json.error || 'Erreur');
      await fetchAll();
      flash('Permissions enregistrées');
    } catch (err: any) {
      setError(err?.message || 'Erreur réseau');
    } finally {
      setSavingPerms(false);
    }
  };

  const permsDirty = useMemo(() => {
    if (!selected) return false;
    const current = new Set(selected.permissions);
    if (current.size !== permDraft.size) return true;
    return Array.from(permDraft).some((c) => !current.has(c));
  }, [selected, permDraft]);

  const infosDirty =
    !!selected &&
    (editName.trim() !== selected.role_name ||
      (editDesc.trim() || '') !== (selected.description ?? '') ||
      editRank !== selected.rank);

  if (loading) return <div className="p-6 text-cockpit-text-2">Chargement…</div>;

  return (
    <div className={embedded ? '' : 'mx-auto max-w-6xl px-4 py-6'}>
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-cockpit-text">Gestion des rôles</h1>
          <p className="text-sm text-cockpit-text-2">
            Créer, renommer, supprimer des rôles et gérer leurs permissions.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate((v) => !v)}
          className="rounded-lg border border-emerald-500/50 bg-emerald-500/20 px-4 py-2 text-sm font-semibold text-emerald-200 transition-colors hover:bg-emerald-500/30"
        >
          + Nouveau rôle
        </button>
      </div>

      {error && <div className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">{error}</div>}
      {notice && <div className="mb-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">{notice}</div>}

      {showCreate && (
        <section className="mb-6 rounded-xl border border-cockpit-line/90 bg-cockpit-panel/90 p-4 backdrop-blur-sm">
          <p className="mb-3 text-sm font-semibold text-cockpit-text">Nouveau rôle</p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              maxLength={50}
              placeholder="Nom du rôle"
              className="flex-1 rounded-lg border border-cockpit-line/60 bg-cockpit-bg-2/30 px-3 py-2 text-sm text-cockpit-text placeholder-cockpit-muted focus:border-emerald-500/50 focus:outline-none"
            />
            <input
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              maxLength={255}
              placeholder="Description (optionnel)"
              className="flex-1 rounded-lg border border-cockpit-line/60 bg-cockpit-bg-2/30 px-3 py-2 text-sm text-cockpit-text placeholder-cockpit-muted focus:border-emerald-500/50 focus:outline-none"
            />
            <button
              type="button"
              onClick={createRole}
              disabled={!newName.trim() || creating}
              className="rounded-lg border border-emerald-500/50 bg-emerald-500/20 px-4 py-2 text-sm font-semibold text-emerald-200 transition-colors hover:bg-emerald-500/30 disabled:opacity-50"
            >
              Créer
            </button>
          </div>
        </section>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[320px_1fr]">
        {/* Liste des rôles */}
        <div className="space-y-2">
          {data?.roles.map((role) => {
            const isSel = role.id === selectedId;
            return (
              <button
                key={role.id}
                type="button"
                onClick={() => setSelectedId(role.id)}
                className={`w-full rounded-lg border px-4 py-3 text-left transition-all ${
                  isSel
                    ? 'border-[#6366f1]/70 bg-[#6366f1]/10'
                    : 'border-cockpit-line/90 bg-cockpit-panel/90 hover:border-[#6366f1]/50 hover:bg-[#6366f1]/5'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-cockpit-text">{role.role_name}</span>
                  <span className="font-mono text-xs text-cockpit-muted">#{role.rank}</span>
                </div>
                <div className="mt-1 flex items-center gap-2 text-xs text-cockpit-text-2">
                  {role.is_system && (
                    <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-amber-200">Protégé</span>
                  )}
                  <span>{role.user_count} utilisateur{role.user_count > 1 ? 's' : ''}</span>
                  <span className="text-cockpit-muted">·</span>
                  <span>{role.is_system ? 'toutes' : role.permissions.length} perm.</span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Détail du rôle sélectionné */}
        <div>
          {!selected ? (
            <div className="rounded-xl border border-cockpit-line/90 bg-cockpit-panel/70 p-8 text-center text-sm text-cockpit-muted">
              Sélectionne un rôle à gauche pour éditer ses informations et ses permissions.
            </div>
          ) : (
            <div className="space-y-6">
              {/* Infos */}
              <section className="rounded-xl border border-cockpit-line/90 bg-cockpit-panel/90 p-4 backdrop-blur-sm">
                <div className="mb-3 flex items-center justify-between">
                  <p className="text-sm font-semibold text-cockpit-text">Informations</p>
                  {selected.is_system && (
                    <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs text-amber-200">
                      Rôle protégé — superutilisateur
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_120px]">
                  <label className="block">
                    <span className="mb-1 block text-xs text-cockpit-muted">Nom</span>
                    <input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      disabled={selected.is_system}
                      maxLength={50}
                      className="w-full rounded-lg border border-cockpit-line/60 bg-cockpit-bg-2/30 px-3 py-2 text-sm text-cockpit-text focus:border-emerald-500/50 focus:outline-none disabled:opacity-50"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs text-cockpit-muted">Rang</span>
                    <input
                      type="number"
                      min={1}
                      value={editRank}
                      onChange={(e) => setEditRank(Number(e.target.value))}
                      disabled={selected.is_system}
                      className="w-full rounded-lg border border-cockpit-line/60 bg-cockpit-bg-2/30 px-3 py-2 text-sm text-cockpit-text focus:border-emerald-500/50 focus:outline-none disabled:opacity-50"
                    />
                  </label>
                </div>
                <label className="mt-3 block">
                  <span className="mb-1 block text-xs text-cockpit-muted">Description</span>
                  <input
                    value={editDesc}
                    onChange={(e) => setEditDesc(e.target.value)}
                    disabled={selected.is_system}
                    maxLength={255}
                    className="w-full rounded-lg border border-cockpit-line/60 bg-cockpit-bg-2/30 px-3 py-2 text-sm text-cockpit-text focus:border-emerald-500/50 focus:outline-none disabled:opacity-50"
                  />
                </label>
                <div className="mt-4 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={deleteRole}
                    disabled={selected.is_system || selected.user_count > 0}
                    title={
                      selected.is_system
                        ? 'Rôle protégé'
                        : selected.user_count > 0
                          ? 'Réassigne les utilisateurs avant de supprimer'
                          : ''
                    }
                    className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-1.5 text-xs font-semibold text-red-200 transition-colors hover:bg-red-500/20 disabled:opacity-40"
                  >
                    Supprimer le rôle
                  </button>
                  <button
                    type="button"
                    onClick={saveInfos}
                    disabled={selected.is_system || !infosDirty || savingInfos}
                    className="rounded-lg bg-[#6366f1]/80 px-4 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-[#6366f1] disabled:opacity-50"
                  >
                    Enregistrer
                  </button>
                </div>
              </section>

              {/* Permissions */}
              <section className="rounded-xl border border-cockpit-line/90 bg-cockpit-panel/90 p-4 backdrop-blur-sm">
                <div className="mb-3 flex items-center justify-between">
                  <p className="text-sm font-semibold text-cockpit-text">Permissions</p>
                  <button
                    type="button"
                    onClick={savePerms}
                    disabled={selected.is_system || !permsDirty || savingPerms}
                    className="rounded-lg bg-[#6366f1]/80 px-4 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-[#6366f1] disabled:opacity-50"
                  >
                    Enregistrer les permissions
                  </button>
                </div>
                {selected.is_system && (
                  <p className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-200/90">
                    Le Founder possède toutes les permissions en permanence (superutilisateur). Rien à éditer.
                  </p>
                )}
                <div className="space-y-4">
                  {catByCategory.map(([cat, perms]) => (
                    <div key={cat}>
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-cockpit-muted">
                        {CATEGORY_LABELS[cat] ?? cat}
                      </p>
                      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                        {perms.map((p) => {
                          const checked = selected.is_system || permDraft.has(p.code);
                          return (
                            <label
                              key={p.code}
                              className={`flex cursor-pointer items-start gap-2 rounded-lg border px-3 py-2 transition-colors ${
                                checked ? 'border-[#6366f1]/50 bg-[#6366f1]/10' : 'border-cockpit-line/60 bg-cockpit-bg-2/30'
                              } ${selected.is_system ? 'cursor-not-allowed opacity-70' : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={selected.is_system}
                                onChange={() => togglePerm(p.code)}
                                className="mt-0.5 accent-[#6366f1]"
                              />
                              <span>
                                <span className="block text-sm text-cockpit-text">{p.label}</span>
                                <span className="block font-mono text-[11px] text-cockpit-muted">{p.code}</span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RolesManagement;
